import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Video, FileText, MessageCircle, Sparkles, CalendarClock } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { CTASection } from '../components/home/CTASection';

const services = [
  {
    id: 'valoracion',
    title: 'Valoración Nutricional Inicial',
    price: '35€',
    description: 'Una consulta individualizada pensada para ayudarte a entender qué enfoque nutricional es el más adecuado antes de iniciar un plan completo.',
    image: '/assets/services/video.webp',
    features: [
      'Realizo estudio previo de las pruebas, informes e historial',
      'Videollamada de 45 minutos donde te explicaré qué necesita tu mascota',
      'Siguientes pasos a seguir según su situación',
      'Resolveremos dudas y preocupaciones que tengas',
      'Orientación sobre el tipo de alimentación más adecuada',
    ],
    details: [
      { icon: Clock, text: 'Duración: 45 minutos' },
      { icon: Video, text: 'Por videollamada' },
      { icon: FileText, text: 'Estudio previo' },
    ],
    highlight: {
      icon: Sparkles,
      text: 'Si después contratas el pack, estos 35€ se descuentan',
    },
    ctaText: 'Reservar valoración',
    ctaLink: 'https://calendly.com/realimenta-naturalvet/cita',
    isExternal: true,
  },
  {
    id: 'pack',
    title: 'Pack Nutricional Específico',
    price: '100€',
    description: 'Servicio completo dirigido a perros y gatos sanos o con patologías. Incluye un plan nutricional individualizado, adaptado al estilo de vida, patologías, y enfocado a mantener su bienestar a largo plazo.',
    image: '/assets/services/seguimiento.webp',
    features: [
      'Valoración Nutricional Inicial',
      'Plan nutricional personalizado',
      'Guía de preparación',
      'Pautas de transición segura día a día',
      'Recomendaciones de suplementos si son necesarios',
    ],
    details: [
      { icon: Clock, text: 'Duración 45 minutos' },
      { icon: Video, text: 'Videollamada' },
      { icon: FileText, text: 'Plan nutricional personalizado' },
    ],
    highlight: {
      icon: Sparkles,
      text: 'Si ya contrataste la Valoración Inicial, los 35€ se descuentan de este pack',
    },
    ctaText: 'Reservar pack completo',
    ctaLink: 'https://calendly.com/realimenta-naturalvet/pack-nutricional-especifico',
    isExternal: true,
  },
];

export const ServiciosPage = () => {
  return (
    <>
      <SEOHead
        title="Servicios de Nutrición Veterinaria Online | REALimenta"
        description="Valoración nutricional inicial (35€) y Pack nutricional específico (100€). Planes personalizados para perros y gatos con seguimiento online."
        canonical="/servicios"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Servicios' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mb-16"
          >
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
              data-testid="servicios-h1"
            >
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
              >
                Servicios
              </span>
              {' '}de nutrición veterinaria online
            </h1>
            <p className="text-text-secondary text-lg md:text-xl">
              Dos opciones para mejorar la alimentación de tu perro o gato, adaptadas a tus necesidades.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative bg-white rounded-3xl border border-brand-sage/30 overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300"
                data-testid={`service-${service.id}`}
              >
                {/* Header with price */}
                <div className="p-8 pb-6 border-b border-brand-sage/20">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#EDF2EA] p-2">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="text-right">
                      <span className="font-manrope text-4xl font-extrabold text-brand-primary">
                        {service.price}
                      </span>
                    </div>
                  </div>
                  
                  <h2 className="font-manrope text-2xl font-bold text-text-primary mb-3">
                    {service.title}
                  </h2>
                  
                  <p className="text-text-secondary">
                    {service.description}
                  </p>
                </div>

                {/* Features */}
                <div className="p-8 pt-6">
                  <h3 className="font-semibold text-text-primary mb-4 text-sm uppercase tracking-wide">
                    ¿Qué incluye?
                  </h3>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={`${service.id}-feature-${i}`} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Details badges */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {service.details.map((detail, i) => (
                      <div 
                        key={`${service.id}-detail-${detail.text}`} 
                        className="flex items-center gap-2 text-xs text-text-muted bg-brand-sage/10 px-3 py-1.5 rounded-full"
                      >
                        <detail.icon className="w-3.5 h-3.5" />
                        {detail.text}
                      </div>
                    ))}
                  </div>

                  {/* Highlight box */}
                  {service.highlight && (
                    <div className="p-4 bg-brand-light-yellow/30 rounded-xl mb-6 flex items-start gap-3">
                      <service.highlight.icon className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-text-primary font-medium">
                        {service.highlight.text}
                      </p>
                    </div>
                  )}

                  {/* CTA */}
                  {service.isExternal ? (
                    <a
                      href={service.ctaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary w-full flex items-center justify-center gap-2 group"
                      data-testid={`service-cta-${service.id}`}
                    >
                      {service.ctaText}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <Link
                      to={service.ctaLink}
                      className="btn-primary w-full flex items-center justify-center gap-2 group"
                      data-testid={`service-cta-${service.id}`}
                    >
                      {service.ctaText}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cancellation Policy */}
      <section className="py-12 bg-brand-sage/10">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-brand-sage/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-sage/20 flex items-center justify-center flex-shrink-0">
                  <CalendarClock className="w-6 h-6 text-brand-primary" />
                </div>
                <div>
                  <h3 className="font-manrope text-lg font-bold text-text-primary mb-3">
                    ¿Qué pasa si tengo que mover o cancelar una cita?
                  </h3>
                  <div className="text-text-secondary space-y-3">
                    <p>
                      A todos nos surgen imprevistos así que ¡no te preocupes! Agradecemos que me avises lo antes posible para poder avisar a otra familia que esté esperando a tener cita.
                    </p>
                    <p>
                      Por ello si vas a necesitar cancelar o mover la sesión, deberás hacerlo con un margen de <strong>1 día laboral antes</strong>.
                    </p>
                    <p className="text-sm bg-amber-50 text-amber-800 p-3 rounded-lg border border-amber-200">
                      <strong>Importante:</strong> En el caso de no ponerse en contacto o avisar con dicho margen, no se devolverá el importe de dicha cita.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default ServiciosPage;
